import { mdiAlarmLight, mdiCashSync, mdiDatabase, mdiShieldCheck } from '@mdi/js'

import type { IconTileGradient } from '../ui/icon-tile'

/**
 * The steering axis (spec 2026-08-24-03): the three families of work a property
 * manager recognises without being taught the internal taxonomy.
 *
 * The mapping from a ticket to its category lives *server side*
 * (`internal/handlers/tickets/category.go`, built from the subtype catalogue) —
 * the frontend never re-derives it. What lives here is the visual identity and
 * the display order, nothing that could drift from the backend.
 *
 * International by construction: these name kinds of work, not national
 * regimes. What a category *contains* varies by country (compliance
 * obligations are not the same in France and in Spain); the category does not.
 */
export const STEERING_CATEGORIES = ['economics', 'incidents', 'compliance'] as const
export type SteeringCategory = (typeof STEERING_CATEGORIES)[number]

/**
 * Data stewardship, not property-manager work — and so not on the steering page
 * at all (spec 2026-08-24-03, lot 10). It lives in the Data section, beside
 * import and export: fixing a conflicting value or a duplicate document is
 * something one does *to the dataset*, not to a building.
 *
 * The code stays here because it is still a category of the server-side axis —
 * the Data-quality page filters on it exactly as the steering page filters on
 * the three.
 */
export const DATA_QUALITY_CATEGORY = 'data_quality'

export type TicketCategory = SteeringCategory | typeof DATA_QUALITY_CATEGORY

export function isSteeringCategory(value: string | undefined): value is SteeringCategory {
    return !!value && (STEERING_CATEGORIES as readonly string[]).includes(value)
}

type CategoryVisual = {
    /** MDI path, drawn through `AppIcon` or `IconTile` — never a raw `<svg>`. */
    icon: string
    /** The family's identity gradient, for the `IconTile` on its KPI card. */
    gradient: IconTileGradient
    /**
     * The token text colour of the family's flat glyphs (spec 2026-09-17-05).
     * Literal Tailwind classes — the scanner cannot follow `text-${x}` — and
     * tokens, not palette shades, so the three read the same in both themes.
     */
    text: string
}

export const CATEGORY_VISUALS: Record<TicketCategory, CategoryVisual> = {
    economics: {
        icon: mdiCashSync,
        gradient: 'economic',
        text: 'text-success',
    },
    incidents: {
        icon: mdiAlarmLight,
        gradient: 'incidents',
        text: 'text-warning',
    },
    compliance: {
        icon: mdiShieldCheck,
        gradient: 'compliance',
        text: 'text-info',
    },
    data_quality: {
        icon: mdiDatabase,
        gradient: 'neutral',
        text: 'text-muted-foreground',
    },
}

/** i18n key for a category's label / one-line gloss. */
export const categoryLabelKey = (c: TicketCategory) => `steering.category_${c}`
export const categoryHintKey = (c: TicketCategory) => `steering.category_${c}_hint`

/**
 * The facet *inside* a category (spec 2026-08-24-03, lot 5) — what the pills
 * under the cards filter on.
 *
 * It replaces the domain, which cut across categories: `service` straddles
 * Economics and Incidents, `insurance` straddles Incidents and Compliance,
 * `document` straddles Compliance and Data quality. A facet that leaks out of
 * the box it is drawn in is not a facet.
 *
 * As with the categories, the ticket → subcategory mapping lives server side
 * (`internal/handlers/tickets/subcategory.go`); this is display order only, and
 * the order is editorial — Incidents follows its lifecycle (reported →
 * dispatched → claimed), the rest run from the heaviest slice down.
 */
export const SUBCATEGORIES_BY_CATEGORY: Record<TicketCategory, readonly string[]> = {
    economics: ['contract_lifecycle', 'service_delivery', 'receivables_payables', 'rental_income', 'resource_consumption'],
    incidents: ['reported_events', 'work_orders', 'claims'],
    // Compliance runs "a duty is due" first, then "a proof is missing".
    // `governance_obligations` is a declared slot with no producer yet, so it
    // simply never renders a pill until something fills it.
    compliance: ['regulatory_controls', 'governance_obligations', 'documentary_obligations', 'insurance_obligations'],
    data_quality: ['completeness', 'consistency', 'document_hygiene'],
}

/** i18n key for a subcategory's pill label. */
export const subcategoryLabelKey = (s: string) => `steering.subcategory_${s}`
